import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../lib/api";
const TOKEN_KEY = "shaycooks_admin_token_v1";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user: null = checking, false = unauthenticated, {} = authenticated
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));

  const setToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setTokenState(t || null);
  }, []);

  // Verify token on mount / token change
  useEffect(() => {
    let mounted = true;
    if (!token) {
      setUser(false);
      return () => { mounted = false; };
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setUser(data);
      } catch (e) {
        if (mounted) {
          setToken(null);
          setUser(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [token, setToken]);

  const login = useCallback(async (username, password) => {
    const { data } = await axios.post(`${API}/admin/login`, { username, password });
    setToken(data.access_token);
    setUser({ username: data.username, role: "admin" });
    return data;
  }, [setToken]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(false);
  }, [setToken]);

  // Helper that returns an axios instance with the Bearer header preset.
  const authedRequest = useMemo(() => {
    return axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  }, [token]);

  const value = useMemo(
    () => ({ user, token, login, logout, authedRequest }),
    [user, token, login, logout, authedRequest]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

// Format FastAPI error detail (can be string, array, or object) into a single string.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
